"use client";

import React, { useState } from "react";
import Link from "next/link";
import {
  FaLinkedin,
  FaTiktok,
  FaYoutube,
  FaFacebook,
  FaInstagram,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import Image from "next/image";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  ArrowRight,
  Heart,
  BookOpen,
  Users,
  Calendar,
  GraduationCap,
  Sparkles,
  CheckCircle,
} from "lucide-react";

// Types
interface FooterLink {
  label: string;
  href: string;
  icon?: React.ReactNode;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  icon: React.ReactNode;
  href: string;
  label: string;
  color: string;
}

// Footer data
const footerSections: FooterSection[] = [
  {
    title: "Quick Links",
    links: [
      { label: "Home", href: "/" },
      { label: "About Us", href: "/about" },
      { label: "Our Chapters", href: "/chapter" },
      { label: "Events", href: "/events" },
      { label: "Gallery", href: "/gallery" },
      { label: "Contact", href: "/contact-us" },
    ],
  },
  {
    title: "Community",
    links: [
      { label: "Alumni Network", href: "/alumni" },
      { label: "Resources Hub", href: "/resources" },
      { label: "Blog & Articles", href: "/blog" },
      { label: "TalentHub", href: "/talenthub" },
      { label: "Mentorship", href: "/alumni?tab=mentorship" },
    ],
  },
  {
    title: "About CONCES",
    links: [
      { label: "Our Mission", href: "/about?tab=mission" },
      { label: "History", href: "/about?tab=history" },
      { label: "Core Pillars", href: "/about?tab=pillars" },
      { label: "Statement of Faith", href: "/about?tab=faith" },
      { label: "National Executives", href: "/executives" },
    ],
  },
  {
    title: "Contact",
    links: [
      { label: "goodnews@conces.org", href: "mailto:goodnews@conces.org" },
      { label: "+2348037503835", href: "tel:+2348037503835" },
      { label: "Contact Page", href: "/contact-us" },
    ],
  },
];

const socialLinks: SocialLink[] = [
  {
    icon: <FaFacebook className="w-5 h-5" />,
    href: "https://www.facebook.com/share/173jPb1P73/",
    label: "Facebook",
    color: "hover:bg-blue-600",
  },
  {
    icon: <FaInstagram className="w-5 h-5" />,
    href: "https://www.instagram.com/concesofficial?igsh=MXZ4aW5wb2Q5M2IxNg==",
    label: "Instagram",
    color: "hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500",
  },
  {
    icon: <FaXTwitter className="w-5 h-5" />,
    href: "https://x.com/concesofficial?t=l3hLqtzs5ZHcgBrUV0PfNw&s=09",
    label: "Twitter",
    color: "hover:bg-gray-900",
  },
  {
    icon: <FaYoutube className="w-5 h-5" />,
    href: "https://youtube.com/@concesofficial",
    label: "YouTube",
    color: "hover:bg-red-600",
  },
  {
    icon: <FaTiktok className="w-5 h-5" />,
    href: "https://www.tiktok.com/@concesofficial?_t=ZS-8zDzAKrmNYj&_r=1",
    label: "TikTok",
    color: "hover:bg-black",
  },
  {
    icon: <FaLinkedin className="w-5 h-5" />,
    href: "https://www.linkedin.com/in/concesofficial?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
    label: "LinkedIn",
    color: "hover:bg-blue-700",
  },
];

const legalLinks = [
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms of Service", href: "/terms" },
  { label: "Cookie Policy", href: "/cookies" },
];

function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    // Simulate subscription
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSubscribed(true);
    setSubscribing(false);
    setEmail("");

    // Reset after 3 seconds
    setTimeout(() => setSubscribed(false), 3000);
  };

  return (
    <footer
      className="relative overflow-hidden"
      role="contentinfo"
      aria-label="Site footer"
    >
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900" />

      {/* Pattern Overlay */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-secondary-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="relative">
        {/* Newsletter Section */}
        {/* <div className="border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary-500/20 rounded-full text-secondary-300 text-sm font-medium mb-4">
                  <Sparkles className="w-4 h-4" />
                  Stay Connected
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Subscribe to Our Newsletter
                </h3>
                <p className="text-gray-300 text-lg">
                  Get updates on events, resources, and community news delivered to your inbox.
                </p>
              </div>
              
              <div>
                {subscribed ? (
                  <div className="flex items-center gap-3 px-6 py-4 bg-green-500/20 border border-green-500/30 rounded-2xl">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                    <span className="text-green-300 font-medium">Thank you for subscribing!</span>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={subscribing}
                      className="px-8 py-4 bg-secondary-500 text-white font-semibold rounded-xl hover:bg-secondary-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      {subscribing ? (
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          Subscribe
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div> */}

        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-8">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <Link href="/" className="inline-block mb-6">
                <div className="flex items-center gap-3">
                  <Image
                    src="/images/logo/logo_white_horizontal.png"
                    alt="conces logo"
                    width={250}
                    height={150}
                  />
                </div>
              </Link>

              <p className="text-gray-300 mb-6 leading-relaxed max-w-md">
                Conference of Nigeria Christian Engineering Students -
                Empowering students through faith-centered education,
                professional development, and community support across all
                chapters nationwide.
              </p>

              {/* Contact moved to footer sections (see Contact section) */}

              {/* Social Links */}
              <div className="flex flex-wrap gap-2">
                {socialLinks.map(({ icon, href, label, color }, index) => (
                  <a
                    key={index}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center text-white transition-all duration-300 ${color} hover:scale-110 hover:shadow-lg`}
                    aria-label={`Follow us on ${label}`}
                  >
                    {icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation Sections */}
            {footerSections.map((section, idx) => (
              <div key={idx}>
                <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider flex items-center gap-2">
                  {idx === 0 && (
                    <ArrowRight className="w-4 h-4 text-secondary-400" />
                  )}
                  {idx === 1 && (
                    <Users className="w-4 h-4 text-secondary-400" />
                  )}
                  {idx === 2 && (
                    <BookOpen className="w-4 h-4 text-secondary-400" />
                  )}
                  {section.title}
                </h4>
                <nav aria-label={section.title}>
                  <ul className="space-y-3">
                    {section.links.map((link, linkIdx) => (
                      <li key={linkIdx}>
                        <Link
                          href={link.href}
                          className="text-gray-300 hover:text-white transition-colors duration-200 text-sm flex items-center gap-2 group"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-600 group-hover:bg-secondary-400 transition-colors" />
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              {/* Copyright */}
              <p className="text-gray-400 text-sm flex items-center gap-2">
                © {new Date().getFullYear()} CONCES. Made with
                <Heart className="w-4 h-4 text-red-400 fill-current" />
                for Engineering Students
              </p>

              {/* Legal Links */}
              <nav aria-label="Legal links">
                <ul className="flex flex-wrap justify-center gap-1 text-sm">
                  {legalLinks.map((item, i) => (
                    <li key={i} className="flex items-center">
                      <Link
                        href={item.href}
                        className="text-gray-400 hover:text-white px-3 py-1 rounded-lg hover:bg-white/5 transition-all"
                      >
                        {item.label}
                      </Link>
                      {i < legalLinks.length - 1 && (
                        <span className="text-gray-600">•</span>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
